import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { isDomesticCoords } from 'src/app/util/geo';

declare var naver: any;

/**
 * 주소 → 좌표. **네이버 → 구글 → (국내 좌표면) 네이버 역지오코딩** 3단.
 *
 * 예전에는 이 로직이 `map.component` 와 상품 페이지 5곳, 총 6곳에 흩어져 있었다. 그중 3곳은
 * `country` 를 `'대한민국'` 으로 하드코딩했고 나머지는 `country` 를 아예 채우지 않았다 —
 * 어느 쪽도 해외 주소를 받을 수 없었다. 한 곳으로 모으면서 구글 폴백을 붙인다.
 *
 * 각 제공자를 자기가 권위 있는 것에만 쓴다:
 *   - 네이버 정방향: 국내 주소를 도로명으로 정규화하고 행정구역까지 준다.
 *   - 구글 정방향  : 네이버가 못 찾는 주소를 찾는다(해외 전부 + 국내 상호명·옛 주소).
 *   - 네이버 역방향: 좌표 → 행정구역. 구글이 채우지 못하는 시군구를 되찾는다.
 *
 * **"네이버 실패 = 해외"가 아니다.** 네이버는 국내에서도 상호명·옛 주소에 0건을 낸다. 그때
 * 구글 결과를 그대로 저장하면 시군구가 빈 문자열로 들어가 지도 검색의 지역 필터에서 상품이
 * 조용히 사라진다(실측 2026-08-16: 국내 47건을 구글에 넣으니 시군구가 47건 전부 공란).
 * 그래서 텍스트가 아니라 **구글이 돌려준 좌표**로 국내/해외를 가른다.
 */

export interface GeocodeHit {
  latitude: number;
  longitude: number;
  /**
   * 주소 입력을 이 값으로 **대체해도 되는** 정규화된 주소. 대체하면 안 되면 `null`.
   *
   * 국내(네이버)는 도로명으로 정규화하는 편이 이득이다. 해외(구글)는 `null` — `formatted_address`
   * 는 요청 언어 하나로만 오는데 상세 화면은 ko/en/ja 3개라, 담당자가 적은 원문을 두고 기존
   * i18n 번역 파이프라인이 처리하게 하는 편이 일관된다.
   */
  normalizedAddress: string | null;
  /** 후보 목록 첫 줄. */
  displayPrimary: string;
  /** 후보 목록 둘째 줄(지번 등). 없으면 빈 문자열. */
  displaySecondary: string;
  country: string;
  sido: string;
  sigugun: string;
  dongmyun: string;
  ri: string;
  rest: string;
  /**
   * 좌표가 번지까지 맞지 않고 상위 행정구역으로 접힌 경우 true.
   *
   * 실측(2026-08-16): `일본 오키나와현 구니가미군 히가시손 타이라 809-1` 을 넣으면 구글은 좌표를
   * 주지만 번지를 버리고 마을(東村) 중심을 돌려준다 — 정확한 위치와 **154m** 차이. 이 사실을
   * 알리지 않으면 담당자는 "검색 성공"으로 보고 넘어가고 상품이 조용히 틀린 곳에 저장된다.
   */
  approximate: boolean;
  /**
   * **국내 좌표인데 행정구역을 채우지 못했다.** 화면이 경고해야 하는 상태.
   * 조용히 저장하면 지역 필터에서 빠지는 상품이 된다. 해외 좌표는 대상이 아니므로 항상 false.
   */
  regionUnresolved: boolean;
}

/** 매퍼의 반환형 — **`source` 가 없다.** 자기 출처를 스스로 주장할 수 없게 해서 오기입을 막는다. */
export type GeocodeResult = { kind: 'hits'; hits: GeocodeHit[] } | { kind: 'empty' } | { kind: 'error' };

/** 오케스트레이션 결과 — 출처는 어느 어댑터를 불렀는지 아는 `geocode()` 만 붙인다. */
export type GeocodeOutcome =
  | { kind: 'hits'; hits: GeocodeHit[]; source: 'naver' | 'google' }
  | { kind: 'empty' }
  | { kind: 'error' };

/** 번지(혹은 그에 준하는) 정밀도로 인정하는 구글 `location_type`. */
const PRECISE_LOCATION_TYPES = ['ROOFTOP', 'RANGE_INTERPOLATED'];

/** 콜백이 이 안에 안 오면 실패로 본다(위 loadGoogleMaps 의 타임아웃 설명). */
const LOAD_TIMEOUT_MS = 15_000;

/** 예외를 값으로 정규화한다(위 geocode 설명). */
async function settle<T>(run: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await run();
  } catch {
    return fallback;
  }
}

const EMPTY_DETAIL = { sido: '', sigugun: '', dongmyun: '', ri: '', rest: '' };

/**
 * 키·도메인 거절은 **스크립트 로드 실패가 아니다.**
 *
 * 리퍼러 화이트리스트에 없는 주소에서 열면(`RefererNotAllowedMapError`) 부트스트랩 스크립트는
 * 정상적으로 받아지고 `callback` 도 불린다 — 거절은 그 뒤 타일을 요청할 때 비동기로 온다.
 * 그래서 `onerror` 와 `try/catch` 만으로는 못 잡고 지도가 회색 상자로 남는다. 구글이 이 경우를
 * 알리는 통로는 전역 `gm_authFailure` 뿐이다. 어드민 호스트가 키 허용목록에 빠지면 정확히 이
 * 모습이 되므로(로컬 4200~4210 은 이번에 추가했다) 여기서 받아 화면에 실패를 알린다.
 */
const authFailureListeners = new Set<() => void>();
let authFailed = false;

function installAuthFailureBridge(): void {
  (window as any).gm_authFailure = () => {
    authFailed = true;
    authFailureListeners.forEach((notify) => notify());
  };
}

@Injectable({ providedIn: 'root' })
export class GeocodeService {
  private googleMapsPromise: Promise<any> | null = null;

  /**
   * 인증 실패 알림을 구독한다. 해지 함수를 돌려준다.
   * **이미 실패한 뒤에 구독해도 즉시 불린다** — 늦게 뜬 화면이 "아직 멀쩡하다"고 오해하지 않게.
   */
  onAuthFailure(listener: () => void): () => void {
    if (authFailed) listener();
    authFailureListeners.add(listener);
    return () => authFailureListeners.delete(listener);
  }

  /**
   * 주소로 좌표를 찾는다.
   *
   * 네이버 **에러**에서도 구글을 시도한다. 여기서 그냥 실패를 돌려주면 담당자가 할 수 있는 일이
   * 없기 때문이다. 정밀도 문제는 지도의 드래그 핀으로 바로잡게 한다.
   */
  async geocode(query: string): Promise<GeocodeOutcome> {
    const trimmed = (query || '').trim();
    if (!trimmed) return { kind: 'empty' };

    // 어댑터 예외를 값으로 정규화한다 — reject 되면 폴백 체인이 통째로 중단돼
    // "네이버가 에러여도 구글은 시도한다"는 약속이 정작 던졌을 때 안 지켜진다.
    const naverResult = await settle(() => this.geocodeWithNaver(trimmed), { kind: 'error' as const });
    if (naverResult.kind === 'hits') return { kind: 'hits', hits: naverResult.hits, source: 'naver' };

    const googleResult = await settle(() => this.geocodeWithGoogle(trimmed), { kind: 'error' as const });
    if (googleResult.kind !== 'hits') {
      // 둘 다 못 찾았다. 한쪽이라도 에러였다면 "결과 없음"이 아니라 "실패"로 알린다 —
      // 주소가 틀린 것과 지오코더가 죽은 것은 담당자가 취할 행동이 다르다.
      if (naverResult.kind === 'error' || googleResult.kind === 'error') return { kind: 'error' };
      return { kind: 'empty' };
    }

    const hits: GeocodeHit[] = [];
    for (const hit of googleResult.hits) hits.push(await this.recoverRegion(hit));
    return { kind: 'hits', hits, source: 'google' };
  }

  /**
   * 국내 좌표인데 행정구역이 비어 있으면 네이버 역지오코딩으로 되찾는다.
   *
   * 해외 좌표는 건드리지 않는다 — sido/sigugun 은 한국 행정구역 컬럼이라 채울 대상이 아니고,
   * 역지오코딩을 불러 봐야 빈 결과만 온다(실측: 오키나와 좌표에 region 없음).
   */
  private async recoverRegion(hit: GeocodeHit): Promise<GeocodeHit> {
    if (!isDomesticCoords(hit.latitude, hit.longitude)) return hit;
    // 복구가 던져도 좌표까지 잃을 이유는 없다 — 복구만 실패한 것으로 본다.
    const region = await settle(() => this.reverseGeocode(hit.latitude, hit.longitude), null);
    // sido 도 함께 본다 — 지역 필터는 sidos 와 sigugun 을 모두 쓴다.
    if (!region?.sigugun || !region.sido) return { ...hit, regionUnresolved: true };
    return { ...hit, ...region, country: region.country || '대한민국', regionUnresolved: false };
  }

  /**
   * 좌표 → 행정구역. 정방향과 같은 `submodules=geocoder` 에 들어 있어 스크립트를 더 싣지 않는다.
   * `rest`(상세 나머지)는 역지오코딩이 주지 않으므로 비워 둔다 — 정방향 경로에서만 채워진다.
   */
  private reverseGeocode(
    lat: number,
    lon: number,
  ): Promise<{ country: string; sido: string; sigugun: string; dongmyun: string; ri: string; rest: string } | null> {
    return new Promise((resolve) => {
      if (typeof naver === 'undefined' || !naver?.maps?.Service?.reverseGeocode) {
        // 스크립트가 아직이면 '주소가 부실하다'가 아니라 '확인할 수 없다'이다. 로그를 남겨
        // 담당자에게 주소를 고치라고 시키는 상황과 구분한다(정방향 어댑터와 같은 원칙).
        console.warn('[GeocodeService] 네이버 SDK 미로드로 역지오코딩 불가', { lat, lon });
        return resolve(null);
      }
      const S = naver.maps.Service;
      S.reverseGeocode(
        {
          coords: new naver.maps.LatLng(lat, lon),
          orders: [S.OrderType.ADDR, S.OrderType.ROAD_ADDR].join(','),
        },
        (status: any, response: any) => {
          if (status !== S.Status.OK) {
            console.warn('[GeocodeService] naver reverse geocode failed', { status, lat, lon });
            return resolve(null);
          }
          const region = response?.v2?.results?.[0]?.region;
          if (!region?.area2?.name) return resolve(null);
          resolve({
            country: '대한민국',
            sido: region.area1?.name ?? '',
            sigugun: region.area2.name,
            dongmyun: region.area3?.name ?? '',
            ri: region.area4?.name ?? '',
            rest: '',
          });
        },
      );
    });
  }

  /**
   * 구글 Maps 스크립트를 지연 로드한다. **`index.html` 에 상시로 싣지 않는 이유**: 해외 주소는
   * 드물어서, 어드민을 여는 모든 사람에게 지도 SDK 를 내려받게 할 값어치가 없다.
   *
   * 약속을 인스턴스에 들고 재사용한다 — 스크립트를 두 번 넣으면 구글이 경고하고 지도가 깨진다.
   */
  loadGoogleMaps(): Promise<any> {
    const w = window as any;
    // 스크립트를 우리가 싣는지와 무관하게 통로를 열어 둔다 — 이미 실린 뒤 이 화면에 들어오면
    // 아래 약속 본문을 안 지나므로 브리지가 안 걸린다.
    installAuthFailureBridge();
    if (w.google?.maps) return Promise.resolve(w.google.maps);
    if (this.googleMapsPromise) return this.googleMapsPromise;

    const apiKey = environment.googleMaps?.apiKey;
    if (!apiKey) return Promise.reject(new Error('environment.googleMaps.apiKey is not set'));

    const READY_CALLBACK = '__nnbAngularGoogleMapsReady';
    this.googleMapsPromise = new Promise((resolve, reject) => {
      // **타임아웃이 없으면 영구 pending 이 된다.** 스크립트가 200 으로 받아지고 콜백이 안 불리는
      // 경우가 실재한다(광고 차단기가 빈 200 을 주거나 프록시가 callback 파라미터를 떼는 경우).
      // 그러면 await 하는 쪽의 catch 도 finally 도 돌지 않아 화면이 조용히 멈춘다.
      const timer = setTimeout(() => {
        this.googleMapsPromise = null;
        reject(new Error(`google maps script did not call back within ${LOAD_TIMEOUT_MS}ms`));
      }, LOAD_TIMEOUT_MS);
      // `loading=async` 에서는 `onload` 가 준비 신호가 아니다 — 그 시점엔 `google.maps` 가 아직
      // 없어서 곧바로 `new maps.Map` 을 부르면 터진다. 준비 신호는 `callback` 뿐이다.
      w[READY_CALLBACK] = () => {
        clearTimeout(timer);
        resolve(w.google.maps);
      };
      // 이미 넣어 둔 태그가 있으면 재사용한다 — 타임아웃은 약속만 비우고 태그는 남기므로,
      // 느린 네트워크에서 재시도하면 부트스트랩이 두 번 들어가 지도가 깨진다.
      const existing = document.querySelector('script[data-nnb-gmaps]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('error', (error) => {
          clearTimeout(timer);
          this.googleMapsPromise = null;
          reject(error);
        });
        return;
      }
      const script = document.createElement('script');
      script.async = true;
      script.dataset.nnbGmaps = '1';
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=${apiKey}` +
        `&libraries=geocoding,marker&language=ko&loading=async&callback=${READY_CALLBACK}`;
      script.onerror = (error) => {
        // 약속을 비워 둔다 — 비워 두지 않으면 한 번의 네트워크 끊김이 이 탭에서 영구 실패로 굳는다.
        clearTimeout(timer);
        this.googleMapsPromise = null;
        reject(error);
      };
      document.head.appendChild(script);
    });
    return this.googleMapsPromise;
  }

  private geocodeWithNaver(query: string): Promise<GeocodeResult> {
    return new Promise((resolve) => {
      if (typeof naver === 'undefined' || !naver?.maps?.Service?.geocode) {
        // 스크립트가 아직이면 '결과 없음'이 아니라 '실패'다 — 구글 폴백은 돌되, 둘 다 실패하면
        // 담당자에게 "오류"라고 말해야 잠시 후 다시 시도할 수 있다.
        resolve({ kind: 'error' });
        return;
      }
      naver.maps.Service.geocode({ query }, (status: any, response: any) => {
        if (status !== naver.maps.Service.Status.OK) {
          console.warn('[GeocodeService] 네이버 지오코딩 실패', { status, query });
          resolve({ kind: 'error' });
          return;
        }
        resolve(mapNaverAddresses(response?.v2?.addresses ?? []));
      });
    });
  }

  private async geocodeWithGoogle(query: string): Promise<GeocodeResult> {
    try {
      const maps = await this.loadGoogleMaps();
      return await new Promise<GeocodeResult>((resolve) => {
        new maps.Geocoder().geocode({ address: query }, (results: any[], status: string) => {
          if (status === 'ZERO_RESULTS') {
            resolve({ kind: 'empty' });
            return;
          }
          if (status !== 'OK' || !results) {
            resolve({ kind: 'error' });
            return;
          }
          resolve(mapGoogleResults(results));
        });
      });
    } catch (e) {
      // 바인딩해서 남긴다 — 이 catch 는 apiKey 미설정(배포 결함), 로더 타임아웃, script onerror,
      // Geocoder 생성 실패를 전부 삼킨다. 로그가 없으면 영구히 깨진 설정과 일시적 네트워크
      // 문제가 화면에서 똑같이 보인다.
      console.error('[GeocodeService] 구글 지오코딩 실패', e);
      return { kind: 'error' };
    }
  }
}

/** 네이버 `v2.addresses` → 후보 목록. 테스트를 위해 서비스 밖에 둔다. */
export function mapNaverAddresses(addresses: any[]): GeocodeResult {
  const hits: GeocodeHit[] = [];
  for (const addr of addresses ?? []) {
    const latitude = Number(addr.y);
    const longitude = Number(addr.x);
    // 비정상 응답으로 좌표가 NaN/Infinity 면 폼 저장과 지도 렌더가 함께 깨진다 — 후보에서 뺀다.
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    const element = (type: string) =>
      (addr.addressElements ?? []).find((e: any) => (e.types ?? []).includes(type))?.longName ?? '';
    hits.push({
      latitude,
      longitude,
      normalizedAddress: addr.roadAddress || addr.jibunAddress || null,
      displayPrimary: addr.roadAddress ?? '',
      displaySecondary: addr.jibunAddress ?? '',
      country: '대한민국',
      sido: element('SIDO'),
      sigugun: element('SIGUGUN'),
      dongmyun: element('DONGMYUN'),
      ri: element('RI'),
      rest: element('REST'),
      approximate: false,
      regionUnresolved: false,
    });
  }
  return hits.length > 0 ? { kind: 'hits', hits } : { kind: 'empty' };
}

/** 구글 `GeocoderResult[]` → 후보 목록. 테스트를 위해 서비스 밖에 둔다. */
export function mapGoogleResults(results: any[]): GeocodeResult {
  const hits: GeocodeHit[] = [];
  for (const r of results ?? []) {
    const latitude = r?.geometry?.location?.lat?.();
    const longitude = r?.geometry?.location?.lng?.();
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    const country = (r.address_components ?? []).find((c: any) => (c.types ?? []).includes('country'));
    hits.push({
      latitude,
      longitude,
      // 담당자가 적은 주소 원문을 유지한다 — 위 normalizedAddress 설명 참고.
      normalizedAddress: null,
      displayPrimary: r.formatted_address ?? '',
      displaySecondary: '',
      country: country?.long_name ?? country?.short_name ?? '',
      ...EMPTY_DETAIL,
      approximate: r.partial_match === true || !PRECISE_LOCATION_TYPES.includes(r?.geometry?.location_type ?? ''),
      // 국내 좌표면 geocode() 가 역지오코딩으로 채운다. 해외면 대상이 아니다.
      regionUnresolved: false,
    });
  }
  return hits.length > 0 ? { kind: 'hits', hits } : { kind: 'empty' };
}
